import { FieldDropdown } from 'blockly/core';

import { beginnerDeviceField, getBeginnerDeviceOptions } from '../devices/device-selector.js';
import type { BeginnerDeviceOption } from '../devices/types.js';
import {
  DISPLAY_POSITION_LINE_COLUMN,
  DISPLAY_POSITION_XY,
} from './oled-fields.js';

/** ST7735 1.8" resolution. */
export const TFT_WIDTH = 128;
export const TFT_HEIGHT = 160;

export const TFT_TEXT_SIZE_OPTIONS: [string, string][] = [
  ['1', '1'],
  ['2', '2'],
  ['3', '3'],
  ['4', '4'],
  ['5', '5'],
];

/**
 * Named color selector for TFT (and future ST7789 / ILI9341) graphics blocks.
 * Values are stable ids for future codegen; labels are student-facing.
 */
export const DISPLAY_COLOR_OPTIONS: [string, string][] = [
  ['Black', 'BLACK'],
  ['White', 'WHITE'],
  ['Red', 'RED'],
  ['Green', 'GREEN'],
  ['Blue', 'BLUE'],
  ['Yellow', 'YELLOW'],
  ['Cyan', 'CYAN'],
  ['Magenta', 'MAGENTA'],
  ['Orange', 'ORANGE'],
  ['Pink', 'PINK'],
  ['Purple', 'PURPLE'],
  ['Brown', 'BROWN'],
  ['Gray', 'GRAY'],
];

/** Hex map for future generators — not shown to students. */
export const DISPLAY_COLOR_HEX: Readonly<Record<string, string>> = {
  BLACK: '#000000',
  WHITE: '#FFFFFF',
  RED: '#FF0000',
  GREEN: '#00FF00',
  BLUE: '#0000FF',
  YELLOW: '#FFFF00',
  CYAN: '#00FFFF',
  MAGENTA: '#FF00FF',
  ORANGE: '#FFA500',
  PINK: '#FFC0CB',
  PURPLE: '#800080',
  BROWN: '#A52A2A',
  GRAY: '#808080',
};

/** TFT device dropdown (available for multi-display later; single TFT blocks omit this). */
export function tftDeviceField(name = 'DEVICE') {
  return beginnerDeviceField(name, 'tft');
}

export function createTftDeviceDropdown(): FieldDropdown {
  return new FieldDropdown((): BeginnerDeviceOption[] => getBeginnerDeviceOptions('tft'));
}

/** Reusable color selector field (JSON shape). */
export function displayColorField(name = 'COLOR', defaultColor = 'WHITE') {
  return {
    type: 'field_dropdown',
    name,
    options: DISPLAY_COLOR_OPTIONS,
    // Blockly JSON ignores unknown keys; default is first option unless set at runtime.
  };
}

/** Position mode JSON field — shared Line/Column vs X/Y. */
export function tftPositionModeField(name = 'POSITION_MODE') {
  return {
    type: 'field_dropdown',
    name,
    options: [
      ['Line / Column', DISPLAY_POSITION_LINE_COLUMN],
      ['X / Y', DISPLAY_POSITION_XY],
    ],
  };
}

export function tftLineField(name = 'LINE', value = 1) {
  return {
    type: 'field_number',
    name,
    value,
    min: 1,
    max: 20,
    precision: 1,
  };
}

export function tftColumnField(name = 'COLUMN', value = 1) {
  return {
    type: 'field_number',
    name,
    value,
    min: 1,
    max: 21,
    precision: 1,
  };
}

export function tftXField(name = 'X', value = 0) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max: TFT_WIDTH - 1,
    precision: 1,
  };
}

export function tftYField(name = 'Y', value = 0) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max: TFT_HEIGHT - 1,
    precision: 1,
  };
}

export function tftSizeField(name: string, value = 20, max = TFT_WIDTH) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max,
    precision: 1,
  };
}

export function tftRadiusField(name = 'RADIUS', value = 5) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max: Math.min(TFT_WIDTH, TFT_HEIGHT),
    precision: 1,
  };
}

export { DISPLAY_POSITION_LINE_COLUMN, DISPLAY_POSITION_XY };
